import { useCallback, useRef } from 'react'
import * as yup from 'yup'

/**
 * Query state carried in the address bar.
 *
 * portal-ux-conventions §3 asks for URL-encoded filters so a filtered view is
 * shareable and survives reload. It matters more here than on a list screen:
 * this is the artefact support hands to engineering, and "the diagnostic for
 * this hotel, this window, this channel" is exactly the thing being handed over.
 */
export type InventoryHealthUrlState = {
  propertyCode?: string
  providerCode?: string
  channelID?: string
  from?: string
  to?: string
  differencesOnly?: boolean
  roomType?: string
  ratePlan?: string
  channelName?: string
  assessment?: string
}

const PARAM = 'filter'

/**
 * The address bar is user-editable and a crafted link is somebody else's input,
 * so every FIELD is validated, not just the top-level shape.
 *
 * Checking only that the payload is an object left each field free to be any
 * JSON type. A non-string reaching a controlled <select>'s value is undefined
 * behaviour for a controlled input, and `differencesOnly` as a non-boolean
 * would travel on to the query. `stripUnknown` also drops keys nobody asked
 * for rather than carrying them into state.
 */
const SCHEMA = yup.object({
  propertyCode: yup.string(),
  providerCode: yup.string(),
  channelID: yup.string(),
  from: yup.string(),
  to: yup.string(),
  differencesOnly: yup.boolean(),
  roomType: yup.string(),
  ratePlan: yup.string(),
  channelName: yup.string(),
  assessment: yup.string()
})

/**
 * Unknown keys are DROPPED, not rejected.
 *
 * yup's noUnknown would fail the whole payload, so a link written before this
 * screen gained a filter — or after it gained another — would lose every
 * filter rather than the one it could not place. Dropping degrades; rejecting
 * discards.
 */
const KNOWN_KEYS = Object.keys(
  SCHEMA.fields
) as (keyof InventoryHealthUrlState)[]

function pickKnown(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).filter(([key]) =>
      KNOWN_KEYS.includes(key as keyof InventoryHealthUrlState)
    )
  )
}

/**
 * Reads once, on mount.
 *
 * A malformed payload must fall back to "no filters" rather than blanking the
 * screen — an exception thrown during render would take the whole screen down
 * rather than just the filters.
 */
export function readUrlState(search: string): InventoryHealthUrlState {
  try {
    const raw = new URLSearchParams(search).get(PARAM)
    if (raw == null || raw === '') {
      return {}
    }

    // URLSearchParams.get() has already decoded the parameter. Decoding it a
    // second time makes legitimate values containing '%' throw here.
    const parsed: unknown = JSON.parse(raw)
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }

    // strict: casting is the wrong behaviour here. yup would turn a numeric
    // roomType into the string "123", which matches no row, so the screen would
    // show an empty table for a link that is malformed rather than restrictive
    // — and say nothing about why. Rejecting instead falls through to the catch
    // and yields no filters, which is what the caller documents and expects.
    return SCHEMA.validateSync(pickKnown(parsed as Record<string, unknown>), {
      strict: true,
      abortEarly: true
    }) as InventoryHealthUrlState
  } catch {
    return {}
  }
}

export function encodeUrlState(state: InventoryHealthUrlState): string {
  const populated = Object.entries(state).filter(
    ([, value]) => value !== undefined && value !== '' && value !== false
  )

  return populated.length === 0
    ? ''
    : JSON.stringify(Object.fromEntries(populated))
}

/**
 * Writes state back to the address bar without adding history entries.
 *
 * replaceState rather than pushState: filtering is not navigation, and one
 * history entry per keystroke would make Back unusable.
 */
export function useInventoryHealthUrlState() {
  const initial = useRef<InventoryHealthUrlState>(
    readUrlState(typeof window === 'undefined' ? '' : window.location.search)
  )

  const write = useCallback((state: InventoryHealthUrlState) => {
    if (typeof window === 'undefined') {
      return
    }

    const encoded = encodeUrlState(state)
    const url = new URL(window.location.href)
    if (encoded === '') {
      url.searchParams.delete(PARAM)
    } else {
      url.searchParams.set(PARAM, encoded)
    }

    window.history.replaceState(null, '', url.toString())
  }, [])

  return { initial: initial.current, write }
}
